import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Plus, Download, LogOut, Pencil, Check, X, FileSpreadsheet } from "lucide-react";
import { LogoIcon } from "@/components/Logo";
import SEO from "@/components/SEO";
import CrudManager from "@/components/admin/CrudManager";
import { HomepageEditor, ActivityLog, MediaLibrary } from "@/components/admin/ExtraPanels";

const FIELD_SETS = {
  products: [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "category", label: "Category", type: "text", placeholder: "Tablets, Capsules…" },
    { key: "tagline", label: "Tagline", type: "text" },
    { key: "description", label: "Description", type: "textarea" },
    { key: "image", label: "Image URL", type: "image" },
    { key: "trending", label: "Trending (Best Seller)", type: "bool", hint: "Show in Trending section" },
    { key: "active", label: "Active", type: "bool", default: true, hint: "Visible on site" },
    { key: "order", label: "Order", type: "number" },
  ],
  banners: [
    { key: "title", label: "Title", type: "text", required: true, placeholder: "Start Your PCD Franchise in 10 Minutes" },
    { key: "subtitle", label: "Subtitle", type: "text" },
    { key: "cta_label", label: "Button label", type: "text", placeholder: "Apply now" },
    { key: "cta_link", label: "Button link", type: "text", placeholder: "#franchise" },
    { key: "priority", label: "Priority (higher shows first)", type: "number" },
    { key: "start_at", label: "Start date (YYYY-MM-DD)", type: "text" },
    { key: "end_at", label: "End date (YYYY-MM-DD)", type: "text" },
    { key: "active", label: "Active", type: "bool", default: true },
  ],
  testimonials: [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "role", label: "Role / Location", type: "text" },
    { key: "quote", label: "Quote", type: "textarea", required: true },
    { key: "image", label: "Photo URL", type: "image" },
    { key: "active", label: "Active", type: "bool", default: true },
    { key: "order", label: "Order", type: "number" },
  ],
  gallery: [
    { key: "title", label: "Title", type: "text" },
    { key: "image", label: "Image/Video URL", type: "image", required: true },
    { key: "kind", label: "Type", type: "select", options: ["image", "video"], default: "image" },
    { key: "category", label: "Category", type: "text" },
    { key: "active", label: "Active", type: "bool", default: true },
    { key: "order", label: "Order", type: "number" },
  ],
  blog_posts: [
    { key: "title", label: "Title", type: "text", required: true },
    { key: "slug", label: "Slug (auto if blank)", type: "text" },
    { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
    { key: "content", label: "Content", type: "textarea", rows: 8, required: true },
    { key: "cover_image", label: "Cover image URL", type: "image" },
    { key: "tags", label: "Tags (comma separated)", type: "text" },
    { key: "seo_title", label: "SEO title", type: "text" },
    { key: "seo_description", label: "SEO description", type: "textarea", rows: 2 },
    { key: "published", label: "Published", type: "bool", default: true },
    { key: "order", label: "Order", type: "number" },
  ],
};

const LEAD_STATUSES = ["new", "contacted", "interested", "converted", "rejected"];
const STATUS_STYLES = {
  new: "bg-[#eef3ee] text-[#1f5d3a] border-[#cfe0d5]",
  contacted: "bg-[#fff5e8] text-[#b26a1a] border-[#f0dcc0]",
  interested: "bg-[#eaf1fb] text-[#2a5aa8] border-[#cddcf3]",
  converted: "bg-[#e8f6ec] text-[#1a7d3a] border-[#c3e6cd]",
  rejected: "bg-[#faeaea] text-[#a83232] border-[#eccaca]",
};

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();

  const [leads, setLeads] = useState([]);
  const [offers, setOffers] = useState([]);
  const [newOffer, setNewOffer] = useState({ title: "", description: "", badge: "", active: true });
  const [editing, setEditing] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/admin", { replace: true }); }, [loading, user, nav]);

  const loadAll = async () => {
    try {
      const [{ data: l }, { data: o }] = await Promise.all([api.get("/admin/leads"), api.get("/admin/offers")]);
      setLeads(l.leads || []);
      setOffers(o.offers || []);
    } catch { toast.error("Failed to load data"); }
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  const doLogout = () => { logout(); nav("/admin", { replace: true }); };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    setLeads((p) => p.filter((l) => l.id !== id));
    toast.success("Lead removed");
  };

  const exportCsv = () => downloadFile("export.csv", "saans_leads.csv");
  const exportXlsx = () => downloadFile("export.xlsx", "saans_leads.xlsx");

  const downloadFile = (endpoint, filename) => {
    const token = localStorage.getItem("saans_token");
    fetch(`${API}/admin/leads/${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const updateStatus = async (id, status) => {
    const prev = leads;
    setLeads((p) => p.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.put(`/admin/leads/${id}/status`, { status });
      toast.success(`Marked ${status}`);
    } catch {
      setLeads(prev);
      toast.error("Failed to update status");
    }
  };

  const createOffer = async (e) => {
    e.preventDefault();
    if (!newOffer.title.trim() || !newOffer.description.trim()) { toast.error("Title and description are required"); return; }
    const { data } = await api.post("/admin/offers", newOffer);
    setOffers((p) => [data, ...p]);
    setNewOffer({ title: "", description: "", badge: "", active: true });
    toast.success("Offer created");
  };

  const saveEdit = async () => {
    const { id, title, description, badge, active } = editing;
    const { data } = await api.put(`/admin/offers/${id}`, { title, description, badge, active });
    setOffers((p) => p.map((o) => (o.id === id ? data : o)));
    setEditing(null);
    toast.success("Offer updated");
  };

  const removeOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/admin/offers/${id}`);
    setOffers((p) => p.filter((o) => o.id !== id));
    toast.success("Offer deleted");
  };

  if (loading || !user) return <div className="min-h-screen bg-[#f9f8f5] p-10 text-[#6b6a65]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#f9f8f5] text-[#202124]" data-testid="admin-dashboard">
      <SEO title="Console — Saans Wellness" description="Admin console" path="/admin/dashboard" noindex />
      <header className="sticky top-0 z-40 border-b border-[#e7e0d6] bg-[#f9f8f5]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoIcon size={32} arc="#1c1a17" sprout="#1f5d3a" />
            <span className="font-display text-xl text-[#202124]">Saans <span className="text-[#d46a1d]">Console</span></span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-[#6b6a65] md:inline">{user.email}</span>
            <button onClick={doLogout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-[#d8cfc1] bg-white px-4 py-2 text-sm font-medium text-[#202124] hover:border-[#d46a1d] hover:text-[#d46a1d]">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          {[
            ["Total leads", leads.length],
            ["New (7 days)", leads.filter((l) => new Date(l.created_at) > new Date(Date.now() - 7 * 864e5)).length],
            ["Active offers", offers.filter((o) => o.active).length],
            ["Categories", 7],
          ].map(([k, v], i) => (
            <div key={i} className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow" data-testid={`stat-${i}`}>
              <div className="text-sm font-medium text-[#6b6a65]">{k}</div>
              <div className="mt-2 font-serif text-4xl text-[#202124]">{v}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leads">
          <TabsList className="mb-8 flex flex-wrap bg-[#f2ece4]">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="banners" data-testid="tab-banners">Banners</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers">Offers</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="homepage" data-testid="tab-homepage">Homepage</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-[#202124]">Franchise Enquiries</h2>
              <div className="flex items-center gap-2">
                <button onClick={exportXlsx} data-testid="export-xlsx"
                  className="inline-flex items-center gap-2 rounded-full border border-[#d8cfc1] bg-white px-4 py-2.5 text-sm font-semibold text-[#1f5d3a] transition-colors hover:border-[#1f5d3a]">
                  <FileSpreadsheet size={15} /> Excel
                </button>
                <button onClick={exportCsv} data-testid="export-csv"
                  className="inline-flex items-center gap-2 rounded-full bg-[#d46a1d] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]">
                  <Download size={15} /> CSV
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-[#e7e0d6] bg-white card-shadow">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#e7e0d6] hover:bg-transparent">
                    <TableHead className="text-[#6b6a65]">Received</TableHead>
                    <TableHead className="text-[#6b6a65]">Name</TableHead>
                    <TableHead className="text-[#6b6a65]">Phone</TableHead>
                    <TableHead className="text-[#6b6a65]">Email</TableHead>
                    <TableHead className="text-[#6b6a65]">Location</TableHead>
                    <TableHead className="text-[#6b6a65]">Business</TableHead>
                    <TableHead className="text-[#6b6a65]">Source</TableHead>
                    <TableHead className="text-[#6b6a65]">Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="py-10 text-center text-[#6b6a65]">No leads yet.</TableCell></TableRow>
                  )}
                  {leads.map((l) => (
                    <TableRow key={l.id} className="border-b border-[#f0ebe3] hover:bg-[#faf8f4]" data-testid={`lead-row-${l.id}`}>
                      <TableCell className="text-sm text-[#6b6a65]">{new Date(l.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-semibold text-[#202124]">{l.name}</TableCell>
                      <TableCell>{l.phone}</TableCell>
                      <TableCell className="text-[#6b6a65]">{l.email || "-"}</TableCell>
                      <TableCell className="text-[#6b6a65]">{[l.city, l.state].filter(Boolean).join(", ") || "-"}</TableCell>
                      <TableCell className="text-[#6b6a65]">{l.current_business || "-"}</TableCell>
                      <TableCell className="text-[11px] uppercase tracking-wide text-[#8a8880]">{l.source || "website"}</TableCell>
                      <TableCell>
                        <select value={l.status || "new"} onChange={(e) => updateStatus(l.id, e.target.value)}
                          data-testid={`lead-status-${l.id}`}
                          className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize outline-none ${STATUS_STYLES[l.status || "new"]}`}>
                          {LEAD_STATUSES.map((s) => <option key={s} value={s} className="bg-white text-[#202124]">{s}</option>)}
                        </select>
                      </TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => removeLead(l.id)} data-testid={`delete-lead-${l.id}`} className="text-[#a8a498] hover:text-[#d46a1d]">
                          <Trash2 size={16} />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="offers">
            <div className="grid gap-8 lg:grid-cols-12">
              <form onSubmit={createOffer} className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow lg:col-span-4" data-testid="create-offer-form">
                <div className="eyebrow text-[#d46a1d]">New Offer</div>
                <h3 className="mt-2 font-serif text-2xl text-[#202124]">Add to homepage</h3>
                <div className="mt-6 space-y-4">
                  <input className="form-field" placeholder="Title" value={newOffer.title}
                    onChange={(e) => setNewOffer((f) => ({ ...f, title: e.target.value }))} data-testid="offer-title-input" />
                  <textarea rows={3} className="form-field resize-none" placeholder="Description" value={newOffer.description}
                    onChange={(e) => setNewOffer((f) => ({ ...f, description: e.target.value }))} data-testid="offer-desc-input" />
                  <input className="form-field" placeholder="Badge (optional)" value={newOffer.badge}
                    onChange={(e) => setNewOffer((f) => ({ ...f, badge: e.target.value }))} data-testid="offer-badge-input" />
                  <label className="flex items-center gap-2 text-sm text-[#4a4a48]">
                    <input type="checkbox" checked={newOffer.active} onChange={(e) => setNewOffer((f) => ({ ...f, active: e.target.checked }))} />
                    Visible on homepage
                  </label>
                  <button type="submit" data-testid="create-offer-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#d46a1d] px-5 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02]">
                    <Plus size={15} /> Publish Offer
                  </button>
                </div>
              </form>

              <div className="lg:col-span-8">
                <h3 className="mb-4 font-serif text-2xl text-[#202124]">All Offers</h3>
                <div className="space-y-4">
                  {offers.length === 0 && <div className="rounded-2xl border border-[#e7e0d6] bg-white p-8 text-center text-[#6b6a65]">No offers yet.</div>}
                  {offers.map((o) => (
                    <div key={o.id} className="rounded-2xl border border-[#e7e0d6] bg-white p-6 card-shadow" data-testid={`offer-row-${o.id}`}>
                      {editing?.id === o.id ? (
                        <div className="space-y-3">
                          <input className="form-field" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                          <textarea rows={2} className="form-field resize-none" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                          <input className="form-field" placeholder="Badge" value={editing.badge || ""} onChange={(e) => setEditing({ ...editing, badge: e.target.value })} />
                          <label className="flex items-center gap-2 text-sm text-[#4a4a48]">
                            <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
                          </label>
                          <div className="flex gap-2">
                            <button onClick={saveEdit} className="inline-flex items-center gap-2 rounded-full bg-[#d46a1d] px-4 py-2 text-sm font-semibold text-white"><Check size={14}/> Save</button>
                            <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 rounded-full border border-[#d8cfc1] px-4 py-2 text-sm font-medium"><X size={14}/> Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-6">
                          <div>
                            <div className="flex flex-wrap items-center gap-3">
                              <h4 className="font-serif text-xl text-[#202124]">{o.title}</h4>
                              {o.badge && <span className="rounded-full bg-[#1f5d3a] px-2.5 py-0.5 text-xs font-semibold text-white">{o.badge}</span>}
                              {!o.active && <span className="rounded-full border border-[#d8cfc1] px-2.5 py-0.5 text-xs font-medium text-[#6b6a65]">Hidden</span>}
                            </div>
                            <p className="mt-2 text-sm text-[#6b6a65]">{o.description}</p>
                          </div>
                          <div className="flex shrink-0 gap-3">
                            <button onClick={() => setEditing(o)} data-testid={`edit-offer-${o.id}`} className="text-[#a8a498] hover:text-[#d46a1d]"><Pencil size={16}/></button>
                            <button onClick={() => removeOffer(o.id)} data-testid={`delete-offer-${o.id}`} className="text-[#a8a498] hover:text-[#d46a1d]"><Trash2 size={16}/></button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="products">
            <div className="mb-6">
              <h2 className="font-serif text-2xl text-[#202124]">Products</h2>
              <p className="text-sm text-[#6b6a65]">Manage the product catalogue shown on the homepage and product pages.</p>
            </div>
            <CrudManager endpoint="products" listKey="products" title="Product" subtitle="Add a product" fields={FIELD_SETS.products} testid="products" />
          </TabsContent>

          <TabsContent value="banners">
            <div className="mb-6">
              <h2 className="font-serif text-2xl text-[#202124]">Promotional Banners</h2>
              <p className="text-sm text-[#6b6a65]">Homepage promo strip — schedule start/end dates, set priority. Higher priority shows first.</p>
            </div>
            <CrudManager endpoint="banners" listKey="banners" title="Banner" subtitle="Homepage promo" fields={FIELD_SETS.banners} testid="banners" />
          </TabsContent>

          <TabsContent value="testimonials">
            <div className="mb-6">
              <h2 className="font-serif text-2xl text-[#202124]">Testimonials</h2>
              <p className="text-sm text-[#6b6a65]">Franchise partner and customer quotes shown on the homepage.</p>
            </div>
            <CrudManager endpoint="testimonials" listKey="testimonials" title="Testimonial" subtitle="Partner quote" fields={FIELD_SETS.testimonials} testid="testimonials" />
          </TabsContent>

          <TabsContent value="gallery">
            <div className="mb-6">
              <h2 className="font-serif text-2xl text-[#202124]">Factory &amp; Gallery</h2>
              <p className="text-sm text-[#6b6a65]">Manufacturing, lab, packaging photos and certificates.</p>
            </div>
            <CrudManager endpoint="gallery" listKey="gallery" title="Media" subtitle="Add a photo/video" fields={FIELD_SETS.gallery} testid="gallery" />
          </TabsContent>

          <TabsContent value="blog">
            <div className="mb-6">
              <h2 className="font-serif text-2xl text-[#202124]">Blog</h2>
              <p className="text-sm text-[#6b6a65]">SEO-friendly articles. Slug auto-generates from the title if left blank.</p>
            </div>
            <CrudManager endpoint="blog_posts" listKey="blog_posts" title="Post" subtitle="Write a blog article" fields={FIELD_SETS.blog_posts} testid="blog" />
          </TabsContent>

          <TabsContent value="media">
            <MediaLibrary />
          </TabsContent>

          <TabsContent value="homepage">
            <div className="mb-6">
              <h2 className="font-serif text-2xl text-[#202124]">Homepage Editor</h2>
              <p className="text-sm text-[#6b6a65]">Hero copy, CTAs and trust metrics displayed on the homepage.</p>
            </div>
            <HomepageEditor />
          </TabsContent>

          <TabsContent value="audit">
            <ActivityLog />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
